
import { CSVUser } from '../types/uploadTypes'

export const parseCSV = (text: string): CSVUser[] => {
  const lines = text.split('\n')
  if (lines.length <= 1) {
    throw new Error('CSV file appears to be empty or only contains headers')
  }
  
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
  
  // Map CSV headers to our expected fields
  const fieldMap: Record<string, string> = {
    'name': 'username',
    'email': 'email',
    'educational background': 'education',
    'location': 'location',
    'country': 'country',
    'languages': 'languages',
    'roles': 'skills',
    'years of experience': 'years_experience',
    'website': 'portfolio_url',
    'biography': 'bio',
    'linkedin link': 'linkedin_username',
    'instagram link': 'instagram_username',
    'facebook link': 'facebook_username',
    'twitter': 'twitter_username'
  }
  
  const users: CSVUser[] = []

  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue
    
    // Handle CSV values with commas inside quotes
    const values: string[] = []
    const line = lines[i]
    let valueStart = 0
    let inQuotes = false

    for (let j = 0; j < line.length; j++) {
      if (line[j] === '"') {
        inQuotes = !inQuotes
      } else if (line[j] === ',' && !inQuotes) {
        values.push(line.substring(valueStart, j).trim().replace(/^"|"$/g, ''))
        valueStart = j + 1
      }
    }
    values.push(line.substring(valueStart).trim().replace(/^"|"$/g, ''))
    
    const user: CSVUser = {}
    
    // Map the CSV values to our user object fields
    headers.forEach((header, index) => {
      const mappedField = fieldMap[header]
      if (mappedField && values[index] && values[index] !== '') {
        if (mappedField === 'years_experience') {
          user[mappedField] = parseInt(values[index], 10) || 0
        } else if (mappedField === 'languages' || mappedField === 'skills') {
          user[mappedField] = values[index]
        } else if (mappedField === 'linkedin_username') {
          // Extract username from LinkedIn URL
          const url = values[index]
          const match = url.match(/linkedin\.com\/in\/([^\/]+)/)
          user[mappedField] = match ? match[1] : url
        } else if (mappedField === 'instagram_username' || mappedField === 'twitter_username' || mappedField === 'facebook_username') {
          // Extract username from social media URLs
          const url = values[index]
          const parts = url.split('/')
          user[mappedField] = parts[parts.length - 1] || url
        } else {
          user[mappedField] = values[index]
        }
      }
    })

    // Make sure we have either email or username for the user
    if ((user.email || user.username)) {
      // Generate an email if it's missing but we have a username
      if (!user.email && user.username) {
        user.email = `${user.username.toLowerCase().replace(/\s+/g, '.')}@example.com`
      }
      
      // Validate email format if present
      if (user.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(user.email)) {
          throw new Error(`Invalid email format at row ${i+1}: ${user.email}`)
        }
      }
      
      // Clean up biography/bio field (remove HTML tags)
      if (user.bio) {
        user.bio = user.bio.replace(/<\/?[^>]+(>|$)/g, '')
      }
      
      users.push(user)
    } else {
      if (i !== lines.length - 1 || lines[i].trim()) { // Skip validation for empty lines at the end
        throw new Error(`Missing required fields at row ${i+1}. Name or email is required.`)
      }
    }
  }

  if (users.length === 0) {
    throw new Error('No valid user data found in CSV')
  }

  return users
}
